import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatchPromise } from '../../utils/AppUtils';
import { useSelector } from 'react-redux';
import { contestSubmissionsSelector } from '../../redux/contest/selectors';
import { MetaTags } from '../../components/Metatags/Metatags';
import { getContestSubmissions } from '../../redux/contest/actions';
import { usePagination } from '../../hook/usePagination';
import { ContestSubmissionsContainer } from '../../components/ScreenContainer/ContestSubmissionsContainer/ContestSubmissionsContainer';
import { IProject } from '../../types/global/helper';
import { useDispatch } from 'react-redux';
import { setmedia } from '../../redux/project/actions';

const ContestSubmissions = () => {
    const params = useParams();
    const dispatchPromise = useDispatchPromise();
    const dispatch = useDispatch();
    const { contestId } = params;
    const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
    const [sortOption, setSortOption] = useState<string>('date');
    const [orderOption, setOrderOption] = useState<'asc' | 'desc'>('desc');
    const { pagination, setPagination, handleRowsChange, handlePageChange } = usePagination();

    // Get data from redux store
    const {
        contestSubmissions,
        loadingSubmissions,
        searchingSubmissions,
        submissionsError,
        submissionsContest,
        availableFilters,
    } = useSelector(contestSubmissionsSelector);

    // Fetch contest details and submissions
    const getData = useCallback(async () => {
        if (contestId) {
            // Then get the submissions
            const response: any = await dispatchPromise(
                getContestSubmissions({
                    contestId,
                    page: pagination.page,
                    limit: pagination.rows,
                    sort: sortOption as any,
                    order: orderOption,
                    ...(!!searchTerm && { searchTerm: searchTerm }),
                }),
            );

            const { pageSize, totalCount, currentPage } = response;

            setPagination({
                page: Number(currentPage),
                rows: pageSize,
                totalCount: totalCount,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contestId, pagination.page, pagination.rows, sortOption, orderOption, searchTerm]);

    // Initial data fetch
    useEffect(() => {
        getData();
    }, [getData]);

    // Handle search input change
    const handleSearchChange = (searchText: string) => {
        setSearchTerm(searchText);
        // Reset to first page when searching
        if (pagination.page !== 1) {
            setPagination(prev => ({
                ...prev,
                page: 1,
            }));
        }
    };

    // Handle sort option change
    const handleSortOptionChange = (option: string) => {
        setSortOption(option);
        // Reset to first page when changing sort
        if (pagination.page !== 1) {
            setPagination(prev => ({
                ...prev,
                page: 1,
            }));
        }
    };

    // Handle order change
    const handleOrderChange = (order: 'asc' | 'desc') => {
        setOrderOption(order);
        // Reset to first page when changing order
        if (pagination.page !== 1) {
            setPagination(prev => ({
                ...prev,
                page: 1,
            }));
        }
    };

    // Handle project play (view project details)
    const handlePlayProject = (project: IProject) => {
        // Navigate to project details or play video
        console.log(project);
        // window.open(`/watch?id=${project.video_id}`, '_blank');
        dispatch(
            setmedia({
                media: project,
                mediaType: 'act',
            }),
        );
    };

    return (
        <MetaTags
            title={`Submissions | ${submissionsContest?.title || 'Contest'}`}
            description={submissionsContest?.description || ''}>
            <ContestSubmissionsContainer
                contest={submissionsContest}
                submissions={contestSubmissions}
                loading={loadingSubmissions}
                searching={searchingSubmissions}
                error={submissionsError}
                pagination={pagination}
                searchTerm={searchTerm}
                sortOption={sortOption}
                orderOption={orderOption}
                availableFilters={availableFilters || []}
                onSearchChange={handleSearchChange}
                onSortOptionChange={handleSortOptionChange}
                onOrderChange={handleOrderChange}
                onRowsChange={handleRowsChange}
                onPageChange={handlePageChange}
                onPlay={handlePlayProject}
                onClearSearch={() => {
                    setSearchTerm(undefined);
                    getData();
                }}
            />
        </MetaTags>
    );
};

export { ContestSubmissions };
